import { useEffect, useRef, useState } from 'react';

import { Building03Icon, UserIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useSelectCandidateContext } from 'hooks/use-select-candidate-context';
import { ProfileCard } from 'layouts/AppPage/ProfileCard/ProfileCard';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import { IconComponent } from 'components/Icon/IconComponent';
import StatusPill from 'components/Pills/StatusPills';
import { UserAvatar } from 'components/UserAvatar/UserAvatar';

import { useLogoutPortalMutation } from 'api/candidate-portal-api';
import {
  authenticatePortal,
  logout,
  selectLinkedCandidates,
  selectPortalAuth,
} from 'api/candidate-portal-slice';

import { CandidatePortalFooter } from './CandidatePortalFooter/CandidatePortalFooter';

const CandidatePortalPage = ({
  children,
  showFooter = true,
  candidate,
  brand,
  showHeader = true,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [showProfile, setShowProfile] = useState(false);
  const profileButtonRef = useRef(null);

  const { isAuthenticated, email, checked } = useSelector(selectPortalAuth);
  const linkedCandidates = useSelector(selectLinkedCandidates);

  // Authenticate on mount (like main app's AppPage)
  useEffect(() => {
    dispatch(authenticatePortal());
  }, [dispatch]);

  // Redirect to login if not authenticated (wait for auth check to complete)
  useEffect(() => {
    if (!checked) return; // Wait for auth check to complete

    if (!isAuthenticated) {
      navigate('/portal/login');
    }
  }, [checked, isAuthenticated, navigate]);

  const { handleSelectCandidate, isLoading } = useSelectCandidateContext();
  const [logoutPortal] = useLogoutPortalMutation();

  const handleLogout = async () => {
    await logoutPortal();
    dispatch(logout());
    navigate('/portal/login');
  };

  const handleKeyDown = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  const handleSelectCandidateKeyDown = (event, linkedCandidate) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelectCandidate(linkedCandidate);
    }
  };

  const brandStyle = brand
    ? {
        '--brand-button-bg': brand.color_button,
        '--brand-button-text': brand.color_button_text,
        '--brand-badge-bg': `${brand.color_button}30`,
        '--brand-link-color': brand.color_link,
      }
    : {};

  const iconButtonClassName = brand?.color_button
    ? 'hover:text-[var(--brand-button-text)] hover:!bg-[var(--brand-button-bg)] hover:border-[var(--brand-button-bg)]'
    : 'hover:bg-primary-5 hover:text-white transition-colors duration-200';

  const badgeClassName = brand?.color_button
    ? 'bg-[var(--brand-button-bg)] text-[var(--brand-button-text)] border-0'
    : 'bg-black text-white';

  const candidateName = candidate ? `${candidate?.first_name} ${candidate?.last_name}` : null;

  const activeCandidates = linkedCandidates?.filter(
    linkedCandidate => !linkedCandidate.canceled && !linkedCandidate.deleted,
  );

  return (
    <div className="min-h-screen bg-surface-secondary pb-16" style={brandStyle}>
      {/* Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-white focus:border focus:border-text-primary focus:rounded focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary">
        {t('candidatePortal.skipToMainContent')}
      </a>

      {/* Header */}
      {showHeader && (
        <header
          className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 sticky top-0 z-50"
          role="banner"
          aria-label={t('candidatePortal.mainNavigation')}>
          <div className="container">
            <div className="relative flex h-28 items-center justify-between w-full">
              {brand?.logo_url ? (
                <img
                  src={brand?.logo_url}
                  alt={t('candidatePortal.clientBrandLogo', { company: brand?.name || '' })}
                  className="w-auto max-h-16"
                />
              ) : (
                <img
                  src={'/assets/images/checkmate/icon-label.svg'}
                  alt={t('candidatePortal.checkmateLogo')}
                  className="w-auto h-12"
                />
              )}

              <nav
                className="flex items-center gap-4 relative"
                aria-label={t('candidatePortal.userMenu')}>
                <div
                  ref={profileButtonRef}
                  role="button"
                  tabIndex={0}
                  aria-label={t('candidatePortal.openUserProfileMenu')}
                  aria-expanded={showProfile}
                  aria-haspopup="true"
                  className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary rounded-full"
                  onClick={() => setShowProfile(true)}
                  onKeyDown={e => handleKeyDown(e, () => setShowProfile(true))}>
                  <UserAvatar
                    className={iconButtonClassName}
                    style={brandStyle}
                    onClick={() => setShowProfile(true)}
                    tooltip={t('candidatePortal.userProfile')}
                    placement="bottom">
                    <HugeiconsIcon icon={UserIcon} size="20" aria-hidden="true" />
                  </UserAvatar>
                </div>
                {activeCandidates && activeCandidates?.length > 1 && (
                  <span
                    className={`flex items-center gap-4 absolute top-0 right-[-7px] px-2 py-1 rounded-full text-sm w-8 h-8 justify-center items-center ${badgeClassName}`}
                    role="status"
                    aria-label={t('candidatePortal.activeApplicationsCount', {
                      count: activeCandidates.length,
                    })}>
                    {activeCandidates.length}
                  </span>
                )}
                <ProfileCard
                  user={{
                    name: candidateName,
                    email: email,
                  }}
                  visible={showProfile}
                  onClose={() => {
                    setShowProfile(prev => {
                      if (prev) setTimeout(() => profileButtonRef.current?.focus(), 100);
                      return false;
                    });
                  }}
                  onSignOut={handleLogout}
                  showViewAccount={false}>
                  <div
                    className="flex flex-col gap-4 p-4"
                    role="menu"
                    aria-label={t('candidatePortal.selectApplication')}>
                    {(activeCandidates || []).map(linkedCandidate => (
                      <div
                        role="menuitem"
                        tabIndex={0}
                        className="flex gap-4 items-center w-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary rounded-lg p-2 -m-2"
                        key={linkedCandidate.link_id || linkedCandidate.candidate_id}
                        onClick={() => handleSelectCandidate(linkedCandidate)}
                        onKeyDown={e => handleSelectCandidateKeyDown(e, linkedCandidate)}
                        aria-label={t('candidatePortal.selectApplicationForCompany', {
                          company: linkedCandidate.company_name,
                        })}>
                        {linkedCandidate.brand_logo_url ? (
                          <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-full">
                            <img
                              src={linkedCandidate.brand_logo_url}
                              alt={t('candidatePortal.companyLogo', {
                                company: linkedCandidate.company_name,
                              })}
                              className="w-14 h-14 object-contain rounded-full"
                            />
                          </div>
                        ) : (
                          <div className="flex-shrink-0" aria-hidden="true">
                            <IconComponent icon={Building03Icon} size="small" strokeWidth={1.5} />
                          </div>
                        )}
                        <div className="flex flex-row items-center gap-8 justify-start">
                          <div className="flex flex-col flex-1">
                            <h3 className="text-xl text-text-primary font-medium m-0">
                              {linkedCandidate.company_name}
                            </h3>
                            <div className="flex sm:gap-2 gap-1 sm:items-center sm:flex-row flex-col">
                              <time
                                className="text-text-tertiary font-light text-lg"
                                dateTime={linkedCandidate.link_created_at}>
                                <Moment fromNow>{linkedCandidate.link_created_at}</Moment>
                              </time>
                            </div>
                          </div>
                          {linkedCandidate.candidate_id === candidate?.id && (
                            <span aria-label={t('candidatePortal.currentlyActiveApplication')}>
                              <StatusPill status="success" text={t('candidatePortal.active')} />
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ProfileCard>
              </nav>
            </div>
          </div>
        </header>
      )}
      <main id="main-content" role="main" aria-label={t('candidatePortal.mainContent')}>
        {children}
      </main>

      {showFooter && <CandidatePortalFooter candidateId={candidate?.id} />}
    </div>
  );
};

export default CandidatePortalPage;
